import type { TableView } from "./export.types";
import { buildZipFile } from "./zip-file";

const XML_DECLARATION = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

const SHEET_NAME_MAX_LENGTH = 31;

const DEFAULT_SHEET_NAME = "Planilha1";

const CONTENT_TYPES = `${XML_DECLARATION}
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

const ROOT_RELATIONSHIPS = `${XML_DECLARATION}
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const WORKBOOK_RELATIONSHIPS = `${XML_DECLARATION}
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

/** Dois estilos: o 0 para os dados e o 1, em negrito, para o cabeçalho. */
const STYLES = `${XML_DECLARATION}
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

const ALLOWED_CONTROL_CODES = [0x09, 0x0a, 0x0d];

/** Caracteres de controle não são aceitos pelo XML e invalidariam a planilha. */
function stripControlChars(value: string): string {
  let cleaned = "";

  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;

    if (code >= 0x20 || ALLOWED_CONTROL_CODES.includes(code)) cleaned += char;
  }

  return cleaned;
}

function escapeXml(value: string): string {
  return stripControlChars(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

export function toColumnLetter(index: number): string {
  let letter = "";
  let current = index;

  while (current >= 0) {
    letter = String.fromCharCode((current % 26) + 65) + letter;
    current = Math.floor(current / 26) - 1;
  }

  return letter;
}

/** O Excel recusa os caracteres reservados e nomes com mais de 31 posições. */
export function toSheetName(title: string): string {
  const cleaned = title.replace(/[[\]:*?/\\]/g, " ").trim();

  return cleaned ? cleaned.slice(0, SHEET_NAME_MAX_LENGTH) : DEFAULT_SHEET_NAME;
}

function toCell(value: string, columnIndex: number, rowNumber: number, style: number): string {
  const reference = `${toColumnLetter(columnIndex)}${rowNumber}`;

  return `<c r="${reference}" s="${style}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

function toRow(cells: string[], rowNumber: number, style: number): string {
  const content = cells
    .map((cell, columnIndex) => toCell(cell, columnIndex, rowNumber, style))
    .join("");

  return `<row r="${rowNumber}">${content}</row>`;
}

export function buildSheetXml({ columns, rows }: TableView): string {
  const header = toRow(columns, 1, 1);
  const body = rows.map((row, index) => toRow(row, index + 2, 0)).join("");

  return `${XML_DECLARATION}
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${header}${body}</sheetData></worksheet>`;
}

function buildWorkbookXml(title: string): string {
  return `${XML_DECLARATION}
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${escapeXml(toSheetName(title))}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;
}

export function buildXlsxFile(view: TableView): Uint8Array {
  const encoder = new TextEncoder();

  return buildZipFile([
    { name: "[Content_Types].xml", content: encoder.encode(CONTENT_TYPES) },
    { name: "_rels/.rels", content: encoder.encode(ROOT_RELATIONSHIPS) },
    { name: "xl/workbook.xml", content: encoder.encode(buildWorkbookXml(view.title)) },
    { name: "xl/_rels/workbook.xml.rels", content: encoder.encode(WORKBOOK_RELATIONSHIPS) },
    { name: "xl/styles.xml", content: encoder.encode(STYLES) },
    { name: "xl/worksheets/sheet1.xml", content: encoder.encode(buildSheetXml(view)) },
  ]);
}
